import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { v4 } from 'uuid';
import { DB } from '~/services/firebase';
import { useAuth } from '~/hooks/auth';

import Dashboard from '~/layouts/Dashboard';
import getValidationErrors from '~/utils/getValidationErrors';
import IComplement from '~/interfaces/IComplement';
import { useToast } from '~/hooks/toast';
import { useProductsCompany } from '~/hooks/productsCompany';
import { bff } from '~/services/bff';

interface Params {
  id: string;
}

interface StateProps {
  item: IComplement;
}

const { REACT_APP_FIREBASE_PROJECT } = process.env;

const ComplementForm: React.FC = () => {
  const history = useHistory();
  const location = useLocation<StateProps>();
  const params = useParams<Params>();
  const { user, company } = useAuth();
  const { addToast } = useToast();
  const { updateComplements, complements } = useProductsCompany();
  const [data, setData] = useState({} as IComplement);
  const [errors, setErrors] = useState({} as any);
  const [loading, setLoading] = useState(false);
  const { id } = params;

  useEffect(() => {
    async function loadComplement() {
      const ref = doc(DB, 'complements', id);
      const docSnap = await getDoc(ref);
      const item = docSnap.data() as IComplement;
      setData({
        ...item,
        uid: id,
      } as IComplement);
    }

    if (location.state && location.state.item) {
      setData(location.state.item);
    } else if (id) {
      loadComplement();
    } else {
      setData({ status: true, type: '0', min: '', max: '', pizzaType: false } as IComplement);
    }
  }, [id, location]);

  const handleSubmit = useCallback(
    async (event?: any) => {
      if (event) {
        event.preventDefault();
      }

      try {
        const schema = Yup.object().shape({
          name: Yup.string().required('O nome é obrigatório'),
          type: Yup.string().required('O tipo é obrigatório'),
          max: Yup.string().required('A escolha máxima é obrigatório'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        setLoading(true);

        const { name, name2, type, status, pizzaType, min, max, order } = data;

        const uid = data.uid || v4();

        const parseData: any = {
          uid,
          name,
          name2,
          type,
          user_id: user.uid,
          company_id: company.uid,
          min: min !== '' ? min : 0,
          max,
          status: status !== undefined ? status : true,
          pizzaType: pizzaType !== undefined ? pizzaType : false,
          items: data.uid ? data.items : [],
          order: order ? parseInt(order) : complements?.length,
        };

        if (data.uid) {
          await updateDoc(doc(DB, 'complements', uid), parseData);
          updateComplements(parseData);
        } else {
          await setDoc(doc(DB, 'complements', uid), parseData);
          updateComplements(parseData, true);
        }

        await bff.delete(`/complements/${REACT_APP_FIREBASE_PROJECT}`);

        addToast({
          type: 'success',
          description: data.uid
            ? 'Grupo de complementos alterado com sucesso!'
            : 'Grupo de complementos criado com sucesso!',
        });

        history.push('/complements');

        setLoading(false);
      } catch (err: any) {
        console.log(err);
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const parseErrors = getValidationErrors(err);
          setErrors(parseErrors);
        }
      }
    },
    [user, data, addToast, company, history, updateComplements],
  );

  return (
    <Dashboard containerMessage="col-xl-6 offset-xl-3 col-md-10 offset-md-1">
      <div className="row">
        <div className="col-xl-6 offset-xl-3 col-md-10 offset-md-1">
          <div className="nk-block nk-block-lg">
            <div className="nk-block-head">
              <div className="nk-block-head-content">
                <div className="nk-block-head-sub">
                  <Link className="back-to" to="/complements">
                    <em className="icon ni ni-arrow-left" />
                    <span>Voltar</span>
                  </Link>
                </div>
                <h4 className="title nk-block-title">
                  {id
                    ? 'Editar Grupo de Complementos'
                    : 'Criar Grupo de Complementos'}
                </h4>
                <div className="nk-block-des">
                  <p>
                    Preencha o formulário abaixo e crie um novo grupo de
                    complementos.
                  </p>
                </div>
              </div>
            </div>
            <div className="card card-bordered">
              <div className="card-inner">
                <form>
                  <div className="row g-2">
                    <div className="col-lg-6">
                      <div className="form-group">
                        <label className="form-label">Nome</label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Nome de exibição"
                            defaultValue={data.name}
                            onChange={event => {
                              setData({ ...data, name: event.target.value });
                            }}
                          />
                          {errors.name && (
                            <span className="invalid">{errors.name}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group">
                        <label className="form-label">
                          Nome de Identificação
                        </label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Nome de Identificação"
                            defaultValue={data.name2}
                            onChange={event => {
                              setData({ ...data, name2: event.target.value });
                            }}
                          />
                          {errors.name2 && (
                            <span className="invalid">{errors.name2}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-4">
                      <div className="form-group">
                        <label className="form-label" htmlFor="default-06">
                          Tipo de complemento
                        </label>
                        <div className="form-control-wrap ">
                          <div className="form-control-select">
                            <select
                              className="form-control"
                              id="default-06"
                              defaultValue={data.type}
                              value={data.type}
                              onChange={event => {
                                setData({
                                  ...data,
                                  type: event.target.value,
                                });
                              }}
                            >
                              <option value="0">Opcional</option>
                              <option value="1">Obrigatório</option>
                            </select>
                            {errors.type && (
                              <span className="invalid">{errors.type}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-4">
                      <div className="form-group">
                        <label className="form-label">Escolha Mínima</label>
                        <div className="form-control-wrap">
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Quantidade mínima para escolha"
                            defaultValue={data.min}
                            disabled={data.type === '0'}
                            onChange={event => {
                              setData({ ...data, min: event.target.value });
                            }}
                          />
                          {errors.min && (
                            <span className="invalid">{errors.min}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-4">
                      <div className="form-group">
                        <label className="form-label">Escolha Máxima</label>
                        <div className="form-control-wrap">
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Quantidade máxima para escolha"
                            defaultValue={data.max}
                            onChange={event => {
                              setData({ ...data, max: event.target.value });
                            }}
                          />
                          {errors.max && (
                            <span className="invalid">{errors.max}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-4">
                      <div className="form-group">
                        <label className="form-label">Ordem</label>
                        <div className="form-control-wrap">
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Ordenar grupo"
                            defaultValue={data.order}
                            onChange={event => {
                              setData({ ...data, order: event.target.value });
                            }}
                          />
                          {errors.order && (
                            <span className="invalid">{errors.order}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-4">
                      <div className="form-group">
                        <label className="form-label">Status?</label>
                        <ul className="custom-control-group g-3 align-center">
                          <li>
                            <div className="custom-control custom-control-sm custom-checkbox">
                              <input
                                id="active"
                                type="checkbox"
                                className="custom-control-input"
                                defaultChecked={data.status}
                                onClick={() => {
                                  setData({
                                    ...data,
                                    status: !data.status,
                                  });
                                }}
                              />
                              <label
                                className="custom-control-label"
                                htmlFor="active"
                              >
                                Ativo
                              </label>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div className="col-lg-4">
                      <div className="form-group">
                        <label className="form-label">Tipo Pizza?</label>
                        <ul className="custom-control-group g-3 align-center">
                          <li>
                            <div className="custom-control custom-control-sm custom-checkbox">
                              <input
                                id="pizzaType"
                                type="checkbox"
                                className="custom-control-input"
                                defaultChecked={data.pizzaType}
                                onClick={() => {
                                  setData({
                                    ...data,
                                    pizzaType: !data.pizzaType,
                                  });
                                }}
                              />
                              <label
                                className="custom-control-label"
                                htmlFor="pizzaType"
                              >
                                Ativo
                              </label>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <button
                          type="button"
                          className="btn btn-lg btn-primary"
                          disabled={loading}
                          onClick={handleSubmit}
                        >
                          {loading && (
                            <div
                              className="spinner-border spinner-border-sm"
                              role="status"
                            />
                          )}
                          {!loading && 'Salvar'}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dashboard>
  );
};

export default ComplementForm;
